import { useMemo, useRef, useState } from 'react';
import { IconCopy, IconEraser, IconSwitchHorizontal } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import {
    REFERENCE_ROWS,
    convertBanglishText,
} from './banglishTypingEngine';
import {
    copyTextToClipboard,
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';

const MODES = ['banglishToBangla', 'banglaToBanglish'];

const textareaClass = 'min-h-[260px] w-full resize-y rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-3 text-[14px] leading-7 text-[#3D3833] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500';

function modeButtonClass(active) {
    return `flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-[13px] font-semibold transition-colors ${
        active
            ? 'border-sky-500 bg-sky-50 text-sky-700 dark:border-sky-500 dark:bg-sky-900/30 dark:text-sky-300'
            : 'border-[#ECE8E4] text-[#6B635C] hover:border-sky-400/50 dark:border-zinc-700 dark:text-zinc-300'
    }`;
}

export default function BanglishTypingText() {
    const { t } = useTranslation();
    const usageRecordedRef = useRef(false);

    const [mode, setMode] = useState(MODES[0]);
    const [inputText, setInputText] = useState('');

    const outputText = useMemo(
        () => convertBanglishText(inputText, mode),
        [inputText, mode],
    );

    const wordCount = inputText.trim() ? inputText.trim().split(/\s+/).length : 0;
    const hasInput = inputText.length > 0;
    const hasOutput = outputText.length > 0;

    const recordUsageOnce = () => {
        if (usageRecordedRef.current) return;
        usageRecordedRef.current = true;
        recordToolUsage('text/banglish-typing', { category: 'text' });
    };

    const handleInputChange = (value) => {
        setInputText(value);
        if (value.trim()) recordUsageOnce();
    };

    const handleModeChange = (nextMode) => {
        if (nextMode === mode) return;
        setMode(nextMode);
        setInputText(outputText || inputText);
    };

    const handleCopy = () => {
        if (!outputText) return;

        if (copyTextToClipboard(outputText)) {
            notifications.show({
                title: t('common.success'),
                message: t('banglishTypingText.copied'),
                color: 'green',
            });
            return;
        }

        void navigator.clipboard?.writeText(outputText)
            .then(() => {
                notifications.show({
                    title: t('common.success'),
                    message: t('banglishTypingText.copied'),
                    color: 'green',
                });
            })
            .catch(() => {
                notifications.show({
                    title: t('common.error'),
                    message: t('banglishTypingText.copyFailed'),
                    color: 'red',
                });
            });
    };

    const handleReset = () => {
        setInputText('');
        usageRecordedRef.current = false;
    };

    const inputLabel = mode === 'banglishToBangla'
        ? t('banglishTypingText.banglishInputLabel')
        : t('banglishTypingText.banglaInputLabel');
    const outputLabel = mode === 'banglishToBangla'
        ? t('banglishTypingText.banglaOutputLabel')
        : t('banglishTypingText.banglishOutputLabel');

    const status = deriveToolStatus({
        hasInput,
        canRun: hasInput,
        hasResult: hasOutput,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`banglishTypingText.howTo.step${i}`)),
        [t],
    );

    const statusText = getToolStatusText(status, t, {
        ready: t('banglishTypingText.statusReady'),
        success: t('banglishTypingText.statusSuccess'),
    });

    return (
        <ToolPageLayout
            slug="text/banglish-typing"
            subtitleKey="banglishTypingText.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('banglishTypingText.inputTitle')}
            howToSteps={howToSteps}
            onReset={hasInput ? handleReset : undefined}
            controls={(
                <ToolCard title={t('banglishTypingText.modeTitle')}>
                    <div className="grid grid-cols-1 gap-2">
                        {MODES.map((item) => (
                            <button
                                key={item}
                                type="button"
                                onClick={() => handleModeChange(item)}
                                className={modeButtonClass(mode === item)}
                            >
                                <IconSwitchHorizontal size={16} stroke={1.9} />
                                {t(`banglishTypingText.modes.${item}`)}
                            </button>
                        ))}
                    </div>
                </ToolCard>
            )}
            settings={(
                <ToolCard
                    title={inputLabel}
                    headerAction={hasInput ? (
                        <button
                            type="button"
                            onClick={handleReset}
                            className="inline-flex items-center gap-1 rounded-lg border border-[#ECE8E4] px-2 py-1 text-[11.5px] font-semibold text-[#6B635C] transition-colors hover:border-sky-400/50 dark:border-zinc-700 dark:text-zinc-300"
                        >
                            <IconEraser size={13} stroke={1.9} />
                            {t('common.reset')}
                        </button>
                    ) : null}
                >
                    <textarea
                        value={inputText}
                        onChange={(e) => handleInputChange(e.target.value)}
                        placeholder={mode === 'banglishToBangla'
                            ? t('banglishTypingText.banglishPlaceholder')
                            : t('banglishTypingText.banglaPlaceholder')}
                        className={textareaClass}
                    />
                    <p className="mt-2 text-right text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                        {t('banglishTypingText.counts', {
                            chars: toBnDigits(inputText.length),
                            words: toBnDigits(wordCount),
                        })}
                    </p>
                </ToolCard>
            )}
            belowWorkArea={mode === 'banglishToBangla' ? (
                <ToolCard title={t('banglishTypingText.referenceTitle')}>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-9">
                        {REFERENCE_ROWS.map(([latin, bengali]) => (
                            <div
                                key={latin}
                                className="flex items-center justify-between gap-2 rounded-lg border border-[#ECE8E4] bg-[#FBFAF9] px-2.5 py-2 dark:border-zinc-700 dark:bg-zinc-800/60"
                            >
                                <span className="font-mono text-[12px] font-bold text-amber-700 dark:text-amber-300">
                                    {latin}
                                </span>
                                <span className="text-right text-[13px] font-semibold text-[#3D3833] dark:text-zinc-100">
                                    {bengali}
                                </span>
                            </div>
                        ))}
                    </div>
                    <p className="mt-3 text-[12px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                        {t('banglishTypingText.referenceTip')}
                    </p>
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('banglishTypingText.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('banglishTypingText.previewEmpty')}
                    idleHint={t('banglishTypingText.previewEmptyHint')}
                    showIdle={!hasInput}
                >
                    <div className="flex h-full min-h-0 flex-col gap-2.5">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {outputLabel}
                            </span>
                            <button
                                type="button"
                                onClick={handleCopy}
                                disabled={!outputText}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#3D3833] transition-colors hover:border-sky-400/50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                <IconCopy size={14} stroke={1.9} />
                                {t('common.copy')}
                            </button>
                        </div>
                        <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-3 text-[14px] leading-7 text-[#3D3833] whitespace-pre-wrap break-words dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100">
                            {outputText || (
                                <span className="text-[#A8A099] dark:text-zinc-500">
                                    {mode === 'banglishToBangla'
                                        ? t('banglishTypingText.banglaOutputPlaceholder')
                                        : t('banglishTypingText.banglishOutputPlaceholder')}
                                </span>
                            )}
                        </div>
                        <p className="text-right text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                            {t('banglishTypingText.characterCount', { count: toBnDigits(outputText.length) })}
                        </p>
                    </div>
                </ToolPreviewPanel>
            )}
        />
    );
}
